import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Card from "components/card";
import AccessButton from "components/button/accessbutton";
import { CATEGORY_ICONS, validateImageAttachment } from "variables/constants"
import { Paperclip, Send, X } from "lucide-react";
import { Form, message } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import Button from "components/button";
import TicketReplyCard from "./TicketReplyCard";
import {
    getTicketReplyList,
    doTicketReplyCreate,
} from "../../../../../redux/ticketreply/ticketreplyState";
import {
    doTicketUpdate
} from "../../../../../redux/supportticket/supportticketState";

export const TKSSTAT_IP = 143;
export const TKSSTAT_RES = 144;

const TicketDetailCard = ({ 
    ticket,
    recordList,
    userId,
    doCreate,
    doSearch,
    pagination,
    filters,
    doTicketUpdate,
    uploading,
    variant = 'it-support'
}) => {
    const [form] = Form.useForm();
    const [markAsResolved, setMarkAsResolved] = useState(false);
    const [attachment, setAttachment] = useState(null); 
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const repliesEndRef = useRef(null);
    const effectiveTicketId = ticket?.tic_id;

    // Configuration based on variant (only for styling)
    const config = {
        'it-support': {
            baseRoute: '/console/it-support',
            gradientFrom: 'from-blue-500',
            gradientTo: 'to-cyan-500',
            gradientLightFrom: 'from-blue-50',
            gradientLightTo: 'to-cyan-50',
            gradientDarkFrom: 'dark:from-blue-900',
            gradientDarkTo: 'dark:to-cyan-900',
            borderColor: 'border-blue-500',
            darkBorderColor: 'dark:border-blue-700',
            buttonGradientFrom: 'from-blue-600',
            buttonGradientTo: 'to-cyan-600',
            buttonHoverFrom: 'hover:from-blue-700',
            buttonHoverTo: 'hover:to-cyan-700',
            checkmarkColor: 'text-blue-600'
        },
        'hr-support': {
            baseRoute: '/console/hr-support',
            gradientFrom: 'from-pink-500',
            gradientTo: 'to-rose-500',
            gradientLightFrom: 'from-pink-50',
            gradientLightTo: 'to-rose-50',
            gradientDarkFrom: 'dark:from-pink-900',
            gradientDarkTo: 'dark:to-rose-900',
            borderColor: 'border-pink-500',
            darkBorderColor: 'dark:border-pink-700',
            buttonGradientFrom: 'from-pink-600',
            buttonGradientTo: 'to-rose-600',
            buttonHoverFrom: 'hover:from-pink-700',
            buttonHoverTo: 'hover:to-rose-700',
            checkmarkColor: 'text-pink-600'
        },
        'partner-service': {
            baseRoute: '/console/partner-service',
            gradientFrom: 'from-purple-500',
            gradientTo: 'to-indigo-500',
            gradientLightFrom: 'from-purple-50',
            gradientLightTo: 'to-indigo-50',
            gradientDarkFrom: 'dark:from-purple-900',
            gradientDarkTo: 'dark:to-indigo-900',
            borderColor: 'border-purple-500',
            darkBorderColor: 'dark:border-purple-700',
            buttonGradientFrom: 'from-purple-600',
            buttonGradientTo: 'to-indigo-600',
            buttonHoverFrom: 'hover:from-purple-700',
            buttonHoverTo: 'hover:to-indigo-700',
            checkmarkColor: 'text-purple-600'
        }
    };

    const currentConfig = config[variant] || config['it-support'];

    const scrollToBottom = () => {
        repliesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (effectiveTicketId) {
            doSearch({
                ...(filters || {}),
                page: 1,
                paginate: pagination?.per_page || 10, 
                ticketId: effectiveTicketId,
            });
        }
    }, [doSearch, effectiveTicketId]);

    useEffect(() => {
        scrollToBottom();
    }, [recordList]);

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        
        if (file) {
            const result = await validateImageAttachment(file, {
                maxSizeMB: 20,
                maxWidth: 8000,
                maxHeight: 8000
            });
            
            if (!result.valid) {
                message.error(result.error);
                e.target.value = '';
                return;
            }
            
            if (result.dimensions && (result.dimensions.width > 4000 || result.dimensions.height > 4000)) {
                message.warning('Large image may affect loading performance', 2);
            }
            
            setAttachment(file);
        }
        e.target.value = '';
    };

    const removeAttachment = () => {
        setAttachment(null);
    };

    if (!ticket) return null

    const {
        tic_code: id,
        tic_title: title,
        submitted_by: submittedBy,
        ticket_priority: priority,
        ticket_status: status,
        ticket_category: category,
        tic_description: description,
        replies_count = recordList?.length,
        department: department,
        tic_department_id: deptId,
        tic_priority_id: priorityId,
        tic_category_id: categoryId,
        tic_title_id: titleId,
        created_by: createdBy,
    } = ticket;

    const Icon = CATEGORY_ICONS[ticket?.ticket_category];
    const isDepartmentMember = userId !== createdBy?.id;
    const isResolved = status?.toLowerCase() === 'resolved';

    const onSubmit = async (formData) => {
        if (isSubmitting) return;
        
        try {
            if (!formData.trp_message?.trim()) {
                message.warning('Please enter a message');
                return;
            }

            const ticketId = ticket?.tic_id || effectiveTicketId;
            
            if (!ticketId) {
                message.error('Ticket ID not found');
                return;
            }

            setIsSubmitting(true);

            const payload = new FormData();
            payload.append('trp_ticket_id', Number(ticketId));
            payload.append('trp_user_id', Number(userId));
            payload.append('trp_message', formData.trp_message);
            payload.append('trp_is_internal', Number(isDepartmentMember ? 0 : 1));
            payload.append('trp_seen', '0');
            
            if (attachment) {
                payload.append('attachment', attachment);
            }

            await doCreate(payload);

            let newStatusId = null;
            if (isDepartmentMember) {
                if (markAsResolved) {
                    newStatusId = TKSSTAT_RES;
                } else if (status?.toLowerCase() === 'pending') {
                    newStatusId = TKSSTAT_IP;
                }
            }

            if (newStatusId && doTicketUpdate) {
                doTicketUpdate({
                    tic_id: ticket.tic_id,
                    tic_title: title,
                    tic_description: description,
                    tic_priority_id: priorityId,    
                    tic_department_id: deptId,
                    tic_category_id: categoryId,
                    tic_title_id: titleId,
                    tic_status_id: newStatusId,
                    modified_by: userId
                });
            }

            setTimeout(() => {
                doSearch({
                    ...(filters || {}),
                    page: 1,
                    paginate: pagination?.per_page || 10, 
                    ticketId: effectiveTicketId,
                });
            }, 500);

            form.resetFields();
            setAttachment(null);
            setMarkAsResolved(false);
            scrollToBottom();
            message.success('Reply sent successfully');
            
        } catch (error) {
            console.error("Reply failed:", error);
            message.error('Failed to send reply. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const canReply = () => {
        if (isResolved) return false;
        if (isDepartmentMember) return true;
        return recordList?.length > 0;
    };

    return (
        <div className="space-y-6 max-w-[1400px] mx-auto">
            <AccessButton
                backLabel="Back to Tickets"
                withBack={true}
                baseRoute={currentConfig.baseRoute}
            />
            <Card className={`bg-white muted-text flex flex-col gap-6 rounded-xl border-2 ${currentConfig.borderColor} shadow-xl overflow-hidden dark-mode-card ${currentConfig.darkBorderColor}`}>
                <div className={`grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 
                                grid-cols-[1fr_auto] [.border-b]:pb-6 bg-gradient-to-br ${currentConfig.gradientLightFrom} ${currentConfig.gradientLightTo} border-b ${currentConfig.gradientDarkFrom} ${currentConfig.gradientDarkTo} dark:border-none`}
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div className={`p-3 rounded-xl bg-gradient-to-br ${currentConfig.gradientFrom} ${currentConfig.gradientTo} text-white shadow-md`}>
                                {Icon && (
                                    <Icon className="w-6 h-6" />
                                )}
                            </div>
                            <div>
                                <h4 className="text-[20px] mb-1 title-text">{title}</h4>
                                <p className="muted-text text-[13px]">
                                    Ticket ID: {id} • Submitted by {submittedBy}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className={`status-indicator uppercase font-semibold ${priority.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {priority}
                                    </span>
                                    <span className={`status-indicator uppercase font-semibold ${status.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {status}
                                    </span>
                                    <span className={`status-indicator text-[10px] ${category.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {category}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pb-6 p-6">
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-[14px] mb-2 title-text">
                                Description
                            </h4>
                            <p className="text-[13px] muted-text">
                                {description}
                            </p>
                        </div>
                        <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                        <div>
                            <h4 className="font-semibold text-[14px] mb-3">
                                Conversation ({replies_count})
                            </h4>
                            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                {recordList?.map((reply) => (
                                   <TicketReplyCard 
                                        key={reply.trp_id} 
                                        {...reply}
                                        department={department}
                                        isCurrentUser={reply.trp_user_id === userId}
                                        isCustomer={reply.trp_user_id === createdBy?.id}
                                    />
                                ))}
                                <div ref={repliesEndRef} />
                            </div>
                            
                            {isResolved && (
                                <div className="text-center py-4 mt-4">
                                    <p className="text-[12px] text-gray-600">
                                        This ticket has been resolved. No further replies are allowed.
                                    </p>
                                </div>
                            )}
                            
                            {canReply() && (
                                <div className="mt-4">
                                    <Form
                                        form={form}
                                        layout="vertical" 
                                        onFinish={onSubmit}
                                    >
                                        <FormItem
                                            name="trp_message"
                                            rules={[requiredField()]}
                                        >
                                            <div className="relative">
                                                <TextArea
                                                    id="trp_message"
                                                    className="input-field"
                                                    maxLength={255}
                                                    placeholder="Type your response..."
                                                    rows={4}
                                                    style={{ paddingBottom: attachment ? '60px' : '' }}
                                                />
                                                {attachment && (
                                                    <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-2 p-3">
                                                        <div className="relative inline-flex items-center gap-2 bg-white rounded-xl pl-3 pr-8 py-2.5 border border-slate-200 hover:border-slate-300 transition-all duration-200 group">
                                                            <div className="p-1.5 rounded-lg bg-slate-100">
                                                                <Paperclip className="w-4 h-4 text-slate-500" />
                                                            </div>
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="text-[13px] text-slate-900 font-semibold truncate max-w-[160px] leading-tight">
                                                                    {attachment.name}
                                                                </span>
                                                                <span className="text-[11px] text-slate-500 leading-tight mt-0.5">
                                                                    {attachment.name.split('.').pop().toUpperCase()} • {(attachment.size / 1024).toFixed(2)}KB
                                                                </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={removeAttachment}
                                                                className="absolute top-1 right-1 p-0.5 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 transition-all opacity-0 group-hover:opacity-100"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}              
                                            </div>
                                        </FormItem>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileSelect}
                                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/tiff,image/heic,image/heif"
                                                    style={{ display: 'none' }}
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={handleAttachClick}
                                                    disabled={uploading || isSubmitting}
                                                    className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all shrink-0 border bg-white text-slate-900 h-8 rounded-lg px-3 gap-2 hover:bg-slate-100 hover:muted-text hover:shadow-none"
                                                >
                                                    <Paperclip className="w-4 h-4" />
                                                    {uploading ? 'Uploading...' : 'Attach File'}
                                                </Button>
                                                {isDepartmentMember && !isResolved && (
                                                    <div className="flex items-center gap-2 ml-4">
                                                        <input
                                                            type="checkbox"
                                                            id="markAsResolved"
                                                            checked={markAsResolved}
                                                            onChange={(e) => setMarkAsResolved(e.target.checked)}
                                                            className="w-4 h-4 rounded-lg border-gray-300"
                                                            style={{ outline: 'none', boxShadow: 'none' }}
                                                        />
                                                        <label htmlFor="markAsResolved" className="text-[11px] text-slate-700 cursor-pointer">
                                                            Mark as resolved
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                            <FormItem 
                                                shouldUpdate
                                                className="mt-3"
                                            >
                                                {() => {
                                                    const message = form.getFieldValue("trp_message");

                                                    return (
                                                        <Button
                                                            htmlType="submit"
                                                            disabled={!message?.trim() || uploading || isSubmitting}
                                                            loading={isSubmitting}
                                                            className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all shrink-0 h-9 px-4 py-2 gap-2 hover:shadow-none ${
                                                                message?.trim() && !uploading && !isSubmitting
                                                                    ? `bg-gradient-to-r ${currentConfig.buttonGradientFrom} ${currentConfig.buttonGradientTo} ${currentConfig.buttonHoverFrom} ${currentConfig.buttonHoverTo} text-white`
                                                                    : `bg-gradient-to-r ${currentConfig.buttonGradientFrom} ${currentConfig.buttonGradientTo} text-white opacity-50`
                                                            }`}
                                                        >
                                                            <Send className="w-4 h-4" />
                                                            Send Reply
                                                        </Button>
                                                    );
                                                }}
                                            </FormItem>
                                        </div>
                                    </Form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}

const mapStateToProps = (state) => ({
    recordList: state.ticketReply.ticketReplyList?.items,
    requesting: state.ticketReply.ticketReplyList?.requesting,
    filters: state.ticketReply.ticketReplyList?.filters,
    success: state.ticketReply.ticketReplyList?.success,
    userId: state.auth?.user?.id,
});

const mapDispatchToProps = (dispatch) => ({
    doSearch: (filter) => dispatch(getTicketReplyList(filter)),
    doCreate: (data) => dispatch(doTicketReplyCreate(data)), 
    doTicketUpdate: (data) => dispatch(doTicketUpdate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketDetailCard);